import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const storedCreds = localStorage.getItem('ssl_monitor_auth');
    if (storedCreds) {
      try {
        const parsed = JSON.parse(storedCreds);
        setCredentials(parsed);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Failed to parse stored credentials:', error);
        localStorage.removeItem('ssl_monitor_auth');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      // Verify credentials by making a test API call
      const authHeader = btoa(`${username}:${password}`);
      
      const response = await fetch(`${API_URL}/statistics`, {
        headers: {
          'Authorization': `Basic ${authHeader}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Store credentials
        const creds = { username, password };
        setCredentials(creds);
        setIsAuthenticated(true);
        localStorage.setItem('ssl_monitor_auth', JSON.stringify(creds));
        return { success: true };
      } else if (response.status === 401) {
        return { success: false, error: 'Invalid username or password' };
      } else {
        return { success: false, error: 'Authentication failed. Please try again.' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please check your connection.' };
    }
  };

  const logout = () => {
    setCredentials(null);
    setIsAuthenticated(false);
    localStorage.removeItem('ssl_monitor_auth');
  };

  const getAuthHeaders = () => {
    if (!credentials) {
      return {
        'Content-Type': 'application/json'
      };
    }
    
    const authHeader = btoa(`${credentials.username}:${credentials.password}`);
    return {
      'Authorization': `Basic ${authHeader}`,
      'Content-Type': 'application/json'
    };
  };

  const value = {
    isAuthenticated,
    isLoading,
    credentials,
    login,
    logout,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
